/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import { isEmpty } from 'lodash';
import React, { Fragment } from 'react';
import { bool, object } from 'prop-types';

import Content, { contentConverter } from 'services/Content';
import Optioner from 'components/shared/Optioner/Optioner';
import { ReactComponent as WarningCircle } from 'assets/icons/warning-circle.svg';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';

const emailAddressCommunicationError = contentConverter.makeHtml(
  Content.emailAddressCommunicationError
);
const noEmailAddressCommunicationError = contentConverter.makeHtml(
  Content.noEmailAddressCommunicationError
);
const NO_EMAIL = 'No email address on file';

const Email = ({ emails }) => <Fragment>{emails}</Fragment>;

const EmailWithPermissions = WithUserPermissions(Email);

export const ValidEmails = ({ emails }) =>
  !isEmpty(emails) && (
    <div className="cmm-modal-content-subgroup cmm-email-address">
      <div className="cmm-modal-content-flex-container">
        <div className="cmm-modal-content-header-container">
          <div className="cmm-modal-content-header">Email Address</div>
          <div className="cmm-modal-content-header-details">
            <div>
              <EmailWithPermissions emails={emails.address} shouldMask />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

ValidEmails.propTypes = {
  emails: object
};

export const InvalidEmails = ({ emails }) =>
  !isEmpty(emails) && (
    <div className="cmm-modal-content-subgroup cmm-content-error">
      <div className="cmm-modal-content-flex-container">
        <div className="cmm-modal-content-header-container">
          <div className="cmm-modal-content-header">Email Address</div>
          <div className="cmm-modal-content-header-details">
            <div className="cmm-modal-icon-container">
              <WarningCircle className="cmm-modal-content-error-warning-icon" />
            </div>
            <div>
              <EmailWithPermissions emails={emails.address} shouldMask />
            </div>
          </div>
        </div>
        <div
          className="cmm-modal-content-message"
          dangerouslySetInnerHTML={{ __html: emailAddressCommunicationError }}
        />
      </div>
    </div>
  );

InvalidEmails.propTypes = {
  emails: object
};

export const NoEmails = () => (
  <div className="cmm-modal-content-subgroup cmm-content-error">
    <div className="cmm-modal-content-flex-container">
      <div className="cmm-modal-content-header-container">
        <div className="cmm-modal-content-header">Email Address</div>
        <div className="cmm-modal-content-header-details">
          <div className="cmm-modal-icon-container">
            <WarningCircle className="cmm-modal-content-error-warning-icon" />
          </div>
          <div>{NO_EMAIL}</div>
        </div>
      </div>
      <div
        className="cmm-modal-content-message"
        dangerouslySetInnerHTML={{ __html: noEmailAddressCommunicationError }}
      />
    </div>
  </div>
);

const EmailAddresses = ({ emailAddresses, errors = {} }) => {
  const {
    hasVerificationInvalidEmail,
    hasVerificationInvalidMailingAndEmail,
    hasVerificationInvalidMailingAndNoEmail,
    hasVerificationNoEmail
  } = errors;
  const hasValidEmail = !(
    hasVerificationInvalidEmail ||
    hasVerificationInvalidMailingAndEmail ||
    hasVerificationInvalidMailingAndNoEmail ||
    hasVerificationNoEmail
  );

  const hasInvalidEmail =
    hasVerificationInvalidEmail || hasVerificationInvalidMailingAndEmail;
  const hasNoEmail =
    hasVerificationNoEmail || hasVerificationInvalidMailingAndNoEmail;

  return (
    <Optioner val={{ hasValidEmail, hasInvalidEmail, hasNoEmail }}>
      {{
        hasValidEmail: <ValidEmails emails={emailAddresses} />,
        hasInvalidEmail: <InvalidEmails emails={emailAddresses} />,
        hasNoEmail: <NoEmails />
      }}
    </Optioner>
  );
};

EmailAddresses.propTypes = {
  errors: object,
  hasNonVerificationError: bool
};

export default EmailAddresses;
